/**
 * Anchor Navigation
 * Handles sticky behavior and active state tracking
 */

export default class AnchorNavigation {
  constructor(element) {
    this.nav = element;
    this.navHeight = 0;
    this.headerHeight = 0;
    this.navOffsetTop = 0;
    this.links = Array.from(this.nav.querySelectorAll('.anchor-nav-item'));
    this.sections = [];
    this.placeholder = null;

    this.init();
  }

  getVisibleHeader() {
    const large = document.querySelector('#header-large');
    const small = document.querySelector('#header-small');
    if (large && large.offsetHeight > 0) return large;
    if (small && small.offsetHeight > 0) return small;
    return large || small || null;
  }

  init() {
    // Get header height
    const header = this.getVisibleHeader();
    this.headerHeight = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);

    // Get initial nav position
    this.navOffsetTop = this.nav.offsetTop;
    this.navHeight = this.nav.offsetHeight;

    // Create placeholder element
    this.createPlaceholder();

    // Map links to their target sections
    this.mapSections();

    // Bind events
    this.bindEvents();

    // Initial check
    this.handleScroll();
  }

  createPlaceholder() {
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'anchor-nav-placeholder';
    this.placeholder.style.height = `${this.navHeight}px`;
    this.nav.parentNode.insertBefore(this.placeholder, this.nav.nextSibling);
  }

  mapSections() {
    this.sections = this.links.map(link => {
      const targetId = link.getAttribute('data-target');
      const section = document.getElementById(targetId);
      return { link, section, targetId };
    }).filter(item => item.section); // Only keep items with valid sections
  }

  bindEvents() {
    // Smooth scroll on click
    this.links.forEach(link => {
      link.addEventListener('click', (e) => this.handleClick(e));
    });

    // Handle scroll
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Debounced resize to avoid thrashing on every pixel
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.handleResize(), 100);
    });
  }

  handleClick(e) {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('data-target');
    const section = document.getElementById(targetId);

    if (section) {
      const offsetTop = section.offsetTop - this.headerHeight - this.navHeight;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }

  handleScroll() {
    const scrollY = window.pageYOffset;

    // Sticky behavior
    if (scrollY >= this.navOffsetTop - this.headerHeight) {
      this.nav.classList.add('is-sticky');
    } else {
      this.nav.classList.remove('is-sticky');
    }

    // Active link tracking
    this.updateActiveLink(scrollY);
  }

  updateActiveLink(scrollY) {
    const offset = this.headerHeight + this.navHeight + 50;
    const viewTop = scrollY + offset;

    // Find the section whose top <= viewTop and whose bottom > viewTop (i.e. currently in view)
    let currentSection = null;
    for (const item of this.sections) {
      const top = item.section.offsetTop;
      const bottom = top + item.section.offsetHeight;
      if (top <= viewTop && bottom > viewTop) {
        currentSection = item;
        break;
      }
    }

    // Update active states
    this.links.forEach(link => link.classList.remove('is-active'));
    if (currentSection) {
      currentSection.link.classList.add('is-active');
    }
  }

  handleResize() {
    const header = this.getVisibleHeader();
    this.headerHeight = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);

    this.navHeight = this.nav.offsetHeight;
    this.placeholder.style.height = `${this.navHeight}px`;

    // When sticky the nav is fixed so the placeholder holds its page position;
    // when not sticky, the nav itself is still in flow so use its offsetTop directly
    if (this.nav.classList.contains('is-sticky')) {
      this.navOffsetTop = this.placeholder.offsetTop;
    } else {
      this.navOffsetTop = this.nav.offsetTop;
    }

    this.handleScroll();
  }
}

// Initialize all anchor navigations
document.addEventListener('DOMContentLoaded', () => {
  const anchorNavs = document.querySelectorAll('[data-anchor-nav]');
  anchorNavs.forEach(nav => new AnchorNavigation(nav));
});
